import React from 'react';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SELECTED_SX = {
    '&.Mui-selected': {
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        '&:hover': { bgcolor: 'primary.dark' },
    },
};

// Hoisted static icon — stable reference across renders (rendering-hoist-jsx)
const dragIndicatorIcon = <DragIndicatorIcon fontSize="small" />;

/**
 * Single saved-filter row with a dedicated drag handle.
 * Extracted as React.memo so that dragging/reordering one item
 * does not re-render siblings (rerender-no-inline-components).
 *
 * Props:
 *   sf               - SavedFilter { id, name }
 *   isActive         - whether this filter is the currently active one
 *   onApply(sf)      - called when the row is clicked
 *   onOpenMenu(e,id) - called when the handle icon is clicked (no drag started)
 */
const FilterListItem = React.memo(function FilterListItem({ sf, isActive, onApply, onOpenMenu }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: sf.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
        zIndex: isDragging ? 1 : undefined,
    };

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            disablePadding
            sx={{
                '& .drag-handle': { opacity: 0, transition: 'opacity 0.15s' },
                '&:hover .drag-handle': { opacity: 1 },
                // Keep visible on touch devices that can't hover
                '@media (hover: none)': { '& .drag-handle': { opacity: 1 } },
                '& .MuiListItem-secondaryAction': { right: 4 },
            }}
            secondaryAction={
                <IconButton
                    ref={setActivatorNodeRef}
                    className="drag-handle"
                    size="small"
                    edge="end"
                    aria-label={`drag to reorder ${sf.name}`}
                    onClick={(e) => { e.stopPropagation(); onOpenMenu(e, sf.id); }}
                    sx={{
                        color: isActive ? 'primary.contrastText' : 'text.secondary',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        touchAction: 'none',
                    }}
                    {...attributes}
                    {...listeners}
                >
                    {dragIndicatorIcon}
                </IconButton>
            }
        >
            <ListItemButton
                selected={isActive}
                onClick={() => onApply(sf)}
                sx={{ ...SELECTED_SX, pr: 5 }}
            >
                <ListItemText
                    primary={sf.name}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                />
            </ListItemButton>
        </ListItem>
    );
});

export default FilterListItem;
